<script setup lang="ts">
import { ref } from 'vue'
import type { AnyRepurchasedContract, OutstandingRepurchaseAction } from '../../../../Types/restructuring'
import RepurchasedContractListItem from './RepurchasedContractListItem.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import RepurchasedContractRemovalConfirmationDialog from './Form/RepurchasedContractRemovalConfirmationDialog.vue'
import RepurchasedContractFormDialog from './Form/RepurchasedContractFormDialog.vue'
import useOutstandingRepurchase from '../../../../Composables/Restructuring/useOutstandingRepurchase.ts'
import { RepurchasedContractType } from '../../../../Constants/RepurchasedContractType.ts'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    readonly?: boolean
    action: OutstandingRepurchaseAction
  }>(),
  { readonly: false },
)

const emit = defineEmits<{
  'update:contracts': [contracts: AnyRepurchasedContract[]]
}>()

const { t, n } = useI18n()

const { contracts, externalContracts, externalRepurchaseAmount, contractRepurchaseAmount, totalRepurchaseAmount } =
  useOutstandingRepurchase(props.action)

const displayFormDialog = ref(false)
const displayRemovalDialog = ref(false)
const currentContractType = ref<RepurchasedContractType>(RepurchasedContractType.default)
const currentContractId = ref<number | null>(null)
const contractToRemove = ref<AnyRepurchasedContract | null>(null)

function showNewContractForm(type: RepurchasedContractType) {
  currentContractType.value = type
  currentContractId.value = null
  displayFormDialog.value = true
}

function showEditContractForm(contract: AnyRepurchasedContract) {
  currentContractType.value = contract.type
  currentContractId.value = contract.id
  displayFormDialog.value = true
}

function askConfirmationToRemoveContract(contract: AnyRepurchasedContract) {
  contractToRemove.value = contract
  displayRemovalDialog.value = true
}

function cancelContractEdition() {
  displayFormDialog.value = false
  currentContractId.value = null
}

function cancelContractRemoval() {
  displayRemovalDialog.value = false
  contractToRemove.value = null
}

function removeContract({ contract }: { contract: AnyRepurchasedContract }) {
  displayRemovalDialog.value = false

  contractToRemove.value = null
  emit(
    'update:contracts',
    props.action.contracts.filter((existingContract) => existingContract.id !== contract.id),
  )
}

function updateContract({ contract }: { contract: AnyRepurchasedContract }) {
  const index = props.action.contracts.findIndex(({ id }) => id === contract.id)
  const contracts = props.action.contracts

  if (index >= 0) {
    contracts[index] = contract
  } else {
    contracts.push(contract)
  }

  emit('update:contracts', contracts)
}
</script>

<template>
  <table class="table table-striped table-hover table-sm">
    <thead>
      <tr>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.number.label') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.client.label') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.repurchaseDate.label') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.refinancer.label') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.agreementNumber.label') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.contract.repurchaseAmount.label') }}
        </th>
        <th v-if="!readonly"></th>
      </tr>
    </thead>
    <tbody>
      <tr class="table-secondary">
        <th colspan="5" class="font-weight-bold">
          {{ t('restructuring.fields.contracts.label') }}
        </th>
        <th class="font-weight-bold" data-test="repurchased-contract-list-contracts-repurchase-amount">
          {{ n(contractRepurchaseAmount, 'currency') }}
        </th>
        <th v-if="!readonly"></th>
      </tr>
      <RepurchasedContractListItem
        v-for="contract of contracts"
        :key="contract.id"
        :contract="contract"
        :readonly="readonly"
        data-test="repurchased-contract-list-item"
        @edit="showEditContractForm(contract)"
        @delete="askConfirmationToRemoveContract(contract)"
      />
      <tr v-if="!readonly">
        <td colspan="7">
          <ButtonAll
            type="action"
            icon="add"
            margin="no"
            size="small"
            label="restructuring.actions.addContract"
            data-test="repurchased-contract-list-add-contract"
            @click="showNewContractForm(RepurchasedContractType.default)"
          />
        </td>
      </tr>
      <tr class="table-secondary">
        <th colspan="5" class="font-weight-bold">
          {{ t('restructuring.fields.outstandingRepurchase.externalContracts.label') }}
        </th>
        <th class="font-weight-bold" data-test="repurchased-contract-list-external-contracts-repurchase-amount">
          {{ n(externalRepurchaseAmount, 'currency') }}
        </th>
        <th v-if="!readonly"></th>
      </tr>
      <RepurchasedContractListItem
        v-for="contract of externalContracts"
        :key="contract.id"
        :contract="contract"
        :readonly="readonly"
        data-test="external-repurchased-contract-list-item"
        @edit="showEditContractForm(contract)"
        @delete="askConfirmationToRemoveContract(contract)"
      />
      <tr v-if="!readonly">
        <td colspan="7">
          <ButtonAll
            type="action"
            icon="add"
            margin="no"
            size="small"
            label="restructuring.actions.addExternalContract"
            data-test="repurchased-contract-list-add-external-contract"
            @click="showNewContractForm(RepurchasedContractType.external)"
          />
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4"></td>
        <td class="text-right font-weight-bold">
          {{ t('restructuring.detail.total_amount.title') }}
        </td>
        <td class="font-weight-bold" data-test="repurchased-contract-list-repurchase-amount">
          {{ n(totalRepurchaseAmount, 'currency') }}
        </td>
        <td v-if="!readonly"></td>
      </tr>
    </tfoot>
  </table>
  <template v-if="!readonly">
    <RepurchasedContractFormDialog
      v-if="displayFormDialog"
      :restructuring-id="action.restructuringId"
      :action-id="action.id"
      :contract-type="currentContractType"
      :contract-id="currentContractId"
      data-test="repurchased-contract-list-form-dialog"
      @close="cancelContractEdition"
      @save="updateContract"
    />
    <RepurchasedContractRemovalConfirmationDialog
      v-if="displayRemovalDialog && contractToRemove"
      :restructuring-id="action.restructuringId"
      :action-id="action.id"
      :contract="contractToRemove"
      data-test="repurchased-contract-list-remove-dialog"
      @close="cancelContractRemoval"
      @delete="removeContract"
    />
  </template>
</template>
<style scoped>
.table-sm {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}
</style>
